import { assert, DefaultMap, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import type { AnalyticsEvents } from "analytics/events";
import type { AppAnalyticsEvents } from "common/analytics/app-events";
import { EmailClient } from "common/components/SerializedApp";
import type { MapLocation, NativeTableSnapshot } from "common/components/types";
import { AppFacilities, MapLocationKind } from "common/components/types";
import { isCustomDomain } from "common/customDomains";
import type { DataSnapshot, PublishedAppSnapshot } from "common/Database";
import {
    publishedAppSnapshotForJSON,
    unzipPublishedAppString,
    unzipString,
    zipPublishedAppString,
} from "common/Database";
import type { TableName } from "common/database-strings";
import { logError, logInfo } from "common/debug-print";
import type { TypeSchema } from "common/description";
import { makeTypeSchema } from "common/description";
import type { AppSnapshotURLs } from "common/firebase-function-types";
import { getAppSnapshotResponseBody } from "common/firebase-function-types";
import { getStaticAppConfig } from "common/static-app";
import { splitEmailAddresses } from "common/strings";
import type { JSONData, JSONObject } from "common/Support";
import { assertNever, isEmptyOrUndefined, maybe, nullToUndefined, parseAnonymousUserID, safeURL } from "common/Support";
import { frontendTrace } from "common/tracing";
import type { YesCodeValue } from "common/yes-code-types";
import type firebase from "firebase/app";
import { isLeft } from "fp-ts/lib/Either";
import { Base64 } from "js-base64";
import { reloadBrowserWindow } from "lib/browser-reload";
import mapValues from "lodash/mapValues";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { ExpiringCache } from "support/expiring-cache";
import {
    localStorageGetItem,
    localStorageRemoveItem,
    localStorageSetItem,
    publishedAppKeyForID,
} from "support/local-storage";
import { trackEvent } from "../../analytics";
import { trackLinkClick } from "../../analytics/link-clicks";
import { browserMightBeOniOS } from "../../lib/browser-detect";
import { getLocationSettings } from "../../lib/location";
import * as routes from "../../routes";
import ActivitySpinner from "../chrome/views/activity-spinner/activity-spinner";
import { hookFirebaseSignOutTransitionEvent, importFirebase, signInToFirebaseWithCustomToken } from "./auth";
import { getUrlHandling, UrlHandling } from "./link-utils";
import { callYesCode } from "./yes-code";

export async function getCurrentUser(): Promise<firebase.User | undefined> {
    const fb = await importFirebase();
    return nullToUndefined(fb.auth().currentUser);
}

export async function addAuthorization(
    headers: { [key: string]: string },
    user: firebase.User
): Promise<{ [key: string]: string }> {
    return { ...headers, Authorization: `Bearer ${await user.getIdToken()}` };
}

interface SnapshotLocations extends AppSnapshotURLs {
    readonly fallbackDataSnapshot?: string;
    readonly fallbackPublishedAppSnapshot?: string;
    readonly fallbackNativeTableSnapshots?: Record<string, string>;
}

interface SnapshotPromises {
    publishedApp?: Promise<PublishedAppSnapshot | undefined>;
    appData?: Promise<DataSnapshot | undefined>;
    readonly nativeTables: Map<string, Promise<NativeTableSnapshot | undefined>>;
}

const storageRegex = /^https:\/\/storage\.googleapis\.com\/glide-[^.]+\.appspot\.com\//;

function replaceCloudStorageWithSameOriginOrAPI(u: string): string {
    const apiEndpoint = getLocationSettings().apiURLPrefix(window.location.hostname);
    let replacementTarget =
        isCustomDomain(window.location.hostname, true) || apiEndpoint.startsWith("/")
            ? `${window.location.origin}/data/`
            : `${apiEndpoint}/data/`;
    if (replacementTarget.startsWith("/")) {
        replacementTarget = `${window.location.origin}${replacementTarget}`;
    }
    return u.replace(storageRegex, replacementTarget);
}

function replaceOriginsForSafariCORSBug(locations: SnapshotLocations): SnapshotLocations {
    // This has two purposes now:
    // 1. Work around a bad Safari CORS bug
    // 2. Work around hostile networks that block storage.googleapis.com
    // The remainder of this comment addresses the first purpose.
    //
    // We _finally_ have a handle on what the bug was.
    // We've historically used Pako to compress snapshots using gzip, and have been
    // labelling the result as "application/gzip". However, we've also been laundering
    // the result through UTF-16 LE strings, which makes the content not exactly "application/gzip".
    //
    // Because the response didn't actually have a valid gzip header, Safari would fail
    // the response under the guise of it being a CORS failure. So we'd proxy the response
    // through nginx endpoints to work around that.
    //
    // For a while, those nginx endpoints liked to fail with partial response bodies. A lot.
    // So we had to try the GCP endpoints first, and Fly second.
    //
    // Fly is now stable enough that we can do it the other way. Sadly, we're still obfuscating
    // snapshots in a way that Safari doesn't like when it's cross-origin so we still have to do this.
    //
    // This is always computed so that we can drop a debugger point here in dev.
    const alteredLocations = {
        dataSnapshot: definedMap(locations.dataSnapshot, replaceCloudStorageWithSameOriginOrAPI),
        publishedAppSnapshot: definedMap(locations.publishedAppSnapshot, replaceCloudStorageWithSameOriginOrAPI),
        nativeTableSnapshots: definedMap(locations.nativeTableSnapshots, r =>
            mapValues(r, replaceCloudStorageWithSameOriginOrAPI)
        ),
        fallbackDataSnapshot: locations.dataSnapshot,
        fallbackPublishedAppSnapshot: locations.publishedAppSnapshot,
        fallbackNativeTableSnapshots: locations.nativeTableSnapshots,
    };

    return alteredLocations;
}

const snapshotHeaderTimeout = 5000;
const snapshotBodyTimeout = 30000;

async function fetchUnzip<T>(url: string, kind: string): Promise<T | undefined> {
    const abortController = new AbortController();
    try {
        const zipped = await frontendTrace("fetchSnapshot", { url, kind }, async fields => {
            const headerTimeoutHandle = setTimeout(() => abortController.abort(), snapshotHeaderTimeout);
            const snapshotResponse = await fetch(url, {
                method: "GET",
                headers: { "Accept-Encoding": "gzip" },
                signal: abortController.signal,
            });
            clearTimeout(headerTimeoutHandle);

            if (!snapshotResponse.ok) return undefined;

            const bodyTimeoutHandle = setTimeout(() => abortController.abort(), snapshotBodyTimeout);
            const text = await snapshotResponse.text();
            clearTimeout(bodyTimeoutHandle);

            fields.zippedLength = text.length;
            return text;
        });

        if (zipped === undefined) return undefined;

        return await frontendTrace(
            "unzipAndParseSnapshot",
            { url, kind, zippedLength: zipped.length },
            async fields => {
                const stringified = unzipString(zipped);
                fields.unzippedLength = stringified.length;
                let result = maybe(() => JSON.parse(stringified), undefined);
                if (result === undefined) {
                    result = maybe(() => JSON.parse(Base64.decode(stringified)), undefined);
                }
                return result;
            }
        );
    } catch (e: unknown) {
        logError(`Could not load ${kind} snapshot`, e);
        return undefined;
    }
}

async function fetchUnzipWithFallback<T>(
    url: string | undefined,
    fallbackURL: string | undefined,
    kind: string
): Promise<T | undefined> {
    let result: T | undefined = url === undefined ? undefined : await fetchUnzip<T>(url, kind);
    if (result === undefined && fallbackURL !== undefined) {
        result = await fetchUnzip<T>(fallbackURL, kind);
    }
    return result;
}

// This is 2.5 minutes. These links expire after 5 minutes so we halve our
// internal expiry to give us some wiggle room.
const snapshotExpiryTime = 150_000;

export class MaterialAppFacilities extends AppFacilities {
    private isIOS = false;
    private snapshotLocationsByAppID: DefaultMap<string, SnapshotLocations> = new DefaultMap(() => ({}));
    private snapshotPromisesByAppID: DefaultMap<string, SnapshotPromises> = new DefaultMap(() => ({
        nativeTables: new Map(),
    }));
    private initialSchemasByAppID = new Map<string, TypeSchema>();

    constructor() {
        super();
        this.isIOS = browserMightBeOniOS;
    }

    public get locationSettings() {
        return getLocationSettings();
    }

    public async fetchJSON(url: string): Promise<JSONData | undefined> {
        try {
            // FIXME: This needs a timeout
            const response = await fetch(url);
            return await response.json();
        } catch (e: unknown) {
            logError(`In MaterialAppFacilities.fetchJSON: ${e}`);
            return undefined;
        }
    }

    public alert(message: string): void {
        alert(message);
    }

    public openURL(url: string): void {
        const safe = safeURL(url);
        if (safe === undefined) return;

        const urlHandling = getUrlHandling(safe, true);
        switch (urlHandling) {
            case UrlHandling.SetWindowHref:
                window.location.href = safe;
                break;
            case UrlHandling.UseATag:
                trackLinkClick(safe);
                window.open(safe);
                break;
            case UrlHandling.UseATagWithBlank:
                trackLinkClick(safe);
                window.open(safe, "_blank");
                break;
            default:
                assertNever(urlHandling);
        }
    }

    public mapURL(location: MapLocation): string {
        if (location.kind === MapLocationKind.LatLong) {
            const { latitude, longitude } = location;
            const url = this.isIOS
                ? `maps://maps.apple.com/?ll=${latitude},${longitude}`
                : `https://www.google.com/maps/?q=${latitude},${longitude}`;

            return url;
        } else {
            const { address } = location;
            const url = this.isIOS
                ? `maps://maps.apple.com/?address=${encodeURIComponent(address)}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            return url;
        }
    }

    public openMap(location: MapLocation): void {
        this.openURL(this.mapURL(location));
    }

    public mailtoURL(
        emailClient: EmailClient,
        to: string | undefined,
        subject: string | undefined,
        body: string | undefined,
        cc: string | undefined,
        bcc: string | undefined
    ): string | undefined {
        function makeEmailList(s: string | undefined) {
            return splitEmailAddresses(s ?? "").join(",");
        }

        to = makeEmailList(to);
        cc = makeEmailList(cc);
        bcc = makeEmailList(bcc);

        if (
            isEmptyOrUndefined(to) &&
            isEmptyOrUndefined(subject) &&
            isEmptyOrUndefined(body) &&
            isEmptyOrUndefined(cc) &&
            isEmptyOrUndefined(bcc)
        ) {
            return undefined;
        }

        const params: { key: string; value: string }[] = [];
        function encodeParams(): string {
            return mapFilterUndefined(params, ({ key, value }) => {
                if (value === "") return undefined;
                return `${key}=${encodeURIComponent(value)}`;
            }).join("&");
        }
        if (subject !== undefined) {
            params.push({ key: "subject", value: subject });
        }
        if (body !== undefined) {
            params.push({ key: "body", value: body });
        }
        if (cc !== undefined) {
            params.push({ key: "cc", value: cc });
        }
        if (bcc !== undefined) {
            params.push({ key: "bcc", value: bcc });
        }
        switch (emailClient) {
            case EmailClient.NativeMail: {
                const encodedTo = encodeURIComponent(to).replace(/%40/g, "@");
                const encodedParams = encodeParams();
                const url = "mailto:" + encodedTo;
                if (encodedParams === "") return url;
                return url + "?" + encodedParams;
            }

            case EmailClient.Gmail:
                params.push({ key: "to", value: to });
                return "googlegmail://co?" + encodeParams();

            case EmailClient.MSOutlook:
                params.push({ key: "to", value: to });
                return "ms-outlook://compose?" + encodeParams();

            default:
                return assertNever(emailClient);
        }
    }

    public callYesCode(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
        return callYesCode(url, params);
    }

    public renderProgress(): React.ReactElement {
        return <ActivitySpinner />;
    }

    public async signInWithCustomToken(token: string): Promise<boolean> {
        const result = await signInToFirebaseWithCustomToken(token);
        if (result.user !== null) {
            await hookFirebaseSignOutTransitionEvent(() => reloadBrowserWindow("Firebase signed us out"));
        }
        return result.user !== null;
    }

    public async isSignedInToFirebase(): Promise<boolean> {
        const currentUser = await getCurrentUser();
        if (currentUser === undefined) return false;
        logInfo("is signed in?", currentUser);
        return parseAnonymousUserID(currentUser.uid) === undefined;
    }

    public async getAuthUserID(): Promise<string | undefined> {
        const user = await getCurrentUser();
        logInfo("current user", user);
        if (user === undefined) return undefined;
        return user.uid;
    }

    public async getAppUserEmail(): Promise<string | undefined> {
        const user = await getCurrentUser();
        if (user === undefined) return undefined;
        return nullToUndefined(user.email);
    }

    public async getIDToken(): Promise<string | undefined> {
        const user = await getCurrentUser();
        if (user === undefined) return undefined;
        return await user.getIdToken();
    }

    // FIXME: This should move out of here and just be a regular function
    public async callAuthCloudFunction(
        functionName: string,
        body: any,
        headers: { [key: string]: string } = {},
        stringify: boolean = true
    ): Promise<Response | undefined> {
        const currentUser = await getCurrentUser();
        if (currentUser === undefined) return undefined;
        return await callCloudFunctionWeb(functionName, body, await addAuthorization(headers, currentUser), stringify);
    }

    public async callAuthIfAvailableCloudFunction(
        functionName: string,
        body: any,
        headers: { [key: string]: string } = {},
        stringify: boolean = true
    ): Promise<Response | undefined> {
        const currentUser = await getCurrentUser();
        if (currentUser !== undefined) {
            headers = await addAuthorization(headers, currentUser);
        }
        return await callCloudFunctionWeb(functionName, body, headers, stringify);
    }

    public setSnapshotLocationsForAppID(
        appID: string,
        { dataSnapshot, publishedAppSnapshot, nativeTableSnapshots }: SnapshotLocations
    ): void {
        this.snapshotLocationsByAppID.set(appID, { dataSnapshot, publishedAppSnapshot, nativeTableSnapshots });
        // There could be some latency between when we download the published app and app data
        // snapshots, so to make everything slightly faster we just immediately attempt to prefetch
        // both of them concurrently.
        const {
            appData: priorAppData,
            publishedApp: priorPublishedApp,
            nativeTables,
        } = this.snapshotPromisesByAppID.get(appID);

        this.snapshotPromisesByAppID.set(appID, {
            appData: dataSnapshot !== undefined ? priorAppData ?? this.backgroundLoadDataSnapshot(appID) : undefined,
            publishedApp:
                routes.isPlayer() && publishedAppSnapshot !== undefined
                    ? priorPublishedApp ?? this.backgroundLoadPublishedAppSnapshot(appID)
                    : undefined,
            nativeTables,
        });
    }

    private snapshotLinkCache = new ExpiringCache<string, Promise<SnapshotLocations>>(snapshotExpiryTime);

    private async getSnapshotLocationsImpl(appID: string): Promise<SnapshotLocations> {
        // This can be called from the builder, too.  The builder loads app
        // data just like apps, by trying to load the snapshot first.  This
        // will also load the schema and make it available as the "initial"
        // schema, which the data store might use.

        // Sometimes we need a _really_ static setup. This means that the snapshots need
        // to be pre-published. We'll set them in the window when this is the case.
        const staticConfig = getStaticAppConfig();
        if (staticConfig !== undefined) {
            const { dataSnapshot, appSnapshot } = staticConfig;
            const { origin } = window.location;
            return {
                dataSnapshot: origin + dataSnapshot,
                publishedAppSnapshot: origin + appSnapshot,
            };
        }

        const locations = this.snapshotLocationsByAppID.get(appID);
        const { dataSnapshot, publishedAppSnapshot, nativeTableSnapshots } = locations;

        if (dataSnapshot !== undefined || publishedAppSnapshot !== undefined || nativeTableSnapshots !== undefined) {
            return replaceOriginsForSafariCORSBug(locations);
        }

        const snapshotURLResponse = await this.callAuthIfAvailableCloudFunction("getAppSnapshot", { appID });
        if (snapshotURLResponse === undefined) {
            return {};
        }

        try {
            const decodedResponse = getAppSnapshotResponseBody.decode(await snapshotURLResponse.json());
            if (isLeft(decodedResponse)) {
                return {};
            }
            const { schema: newSchema } = decodedResponse.right;
            this.setSnapshotLocationsForAppID(appID, decodedResponse.right);
            if (newSchema !== undefined) {
                this.setInitialSchemaForAppID(appID, makeTypeSchema(newSchema.tables));
            }
            return replaceOriginsForSafariCORSBug(decodedResponse.right);
        } catch {
            // FIXME: Maybe we should log this
            return {};
        }
    }

    private getSnapshotLocations(appID: string): Promise<SnapshotLocations> {
        return this.snapshotLinkCache.getOrCreate(appID, () => [
            this.getSnapshotLocationsImpl(appID),
            new Date(Date.now() + snapshotExpiryTime),
        ]);
    }

    private async backgroundLoadDataSnapshot(appID: string): Promise<DataSnapshot | undefined> {
        const { dataSnapshot, fallbackDataSnapshot } = await this.getSnapshotLocations(appID);
        return await fetchUnzipWithFallback(dataSnapshot, fallbackDataSnapshot, "data");
    }

    public async loadDataSnapshot(appID: string): Promise<DataSnapshot | undefined> {
        const removeOnFailure = async (promise: Promise<DataSnapshot | undefined>) => {
            try {
                return await promise;
            } catch (e: unknown) {
                this.snapshotPromisesByAppID.get(appID).appData = undefined;
                throw e;
            }
        };

        let { appData: appDataPromise } = this.snapshotPromisesByAppID.get(appID);
        if (appDataPromise !== undefined) {
            return removeOnFailure(appDataPromise);
        }
        appDataPromise = this.backgroundLoadDataSnapshot(appID);
        this.snapshotPromisesByAppID.get(appID).appData = appDataPromise;
        return removeOnFailure(appDataPromise);
    }

    public purgeDataSnapshot(appID: string): void {
        this.snapshotLocationsByAppID.delete(appID);
        this.snapshotPromisesByAppID.delete(appID);
    }

    public loadPublishedAppFromLocalStorage(appID: string): PublishedAppSnapshot | undefined {
        const locally = localStorageGetItem(publishedAppKeyForID(appID));
        return definedMap(locally, unzipPublishedAppString);
    }

    private async backgroundLoadPublishedAppSnapshot(appID: string): Promise<PublishedAppSnapshot | undefined> {
        const publishedAppFromLocalStorage = this.loadPublishedAppFromLocalStorage(appID);
        const olderThan = publishedAppFromLocalStorage?.publishedAt;

        const { publishedAppSnapshot, fallbackPublishedAppSnapshot } = await this.getSnapshotLocations(appID);

        const firstSnapshot = publishedAppSnapshot ?? fallbackPublishedAppSnapshot;
        if (firstSnapshot === undefined) {
            return publishedAppFromLocalStorage;
        }

        const publishedAppSnapshotURL = new URL(firstSnapshot);
        const hashParameters = new URLSearchParams(publishedAppSnapshotURL.hash.substring(1));
        const remotePublishedAt = hashParameters.get("PublishedAt");
        if (
            remotePublishedAt !== null &&
            olderThan !== undefined &&
            Number.parseInt(remotePublishedAt, 10) <= olderThan.getTime()
        ) {
            return publishedAppFromLocalStorage;
        }

        const response = await fetchUnzipWithFallback<JSONObject>(
            publishedAppSnapshot,
            fallbackPublishedAppSnapshot,
            "published app"
        );

        if (response === undefined) return response;
        const publishedApp = publishedAppSnapshotForJSON(response);
        this.persistPublishedApp(appID, publishedApp);
        return publishedApp;
    }

    private async backgroundLoadNativeTableSnapshot(
        appID: string,
        tableName: TableName
    ): Promise<NativeTableSnapshot | undefined> {
        assert(!tableName.isSpecial);

        const { nativeTableSnapshots, fallbackNativeTableSnapshots } = await this.getSnapshotLocations(appID);
        const snapshotURL = nativeTableSnapshots?.[tableName.name];
        const fallbackSnapshotURL = fallbackNativeTableSnapshots?.[tableName.name];

        return fetchUnzipWithFallback(snapshotURL, fallbackSnapshotURL, "native table");
    }

    public loadPublishedAppSnapshot(appID: string): Promise<PublishedAppSnapshot | undefined> {
        const awaitAndRemove = async (promise: Promise<PublishedAppSnapshot | undefined>) => {
            try {
                return await promise;
            } finally {
                this.snapshotPromisesByAppID.get(appID).publishedApp = undefined;
            }
        };

        let { publishedApp: publishedAppPromise } = this.snapshotPromisesByAppID.get(appID);
        if (publishedAppPromise !== undefined) {
            return awaitAndRemove(publishedAppPromise);
        }

        publishedAppPromise = this.backgroundLoadPublishedAppSnapshot(appID);
        this.snapshotPromisesByAppID.get(appID).publishedApp = publishedAppPromise;
        return awaitAndRemove(publishedAppPromise);
    }

    public async loadNativeTableSnapshot(
        appID: string,
        tableName: TableName
    ): Promise<NativeTableSnapshot | undefined> {
        if (tableName.isSpecial) return undefined;

        const existingPromise = this.snapshotPromisesByAppID.get(appID).nativeTables.get(tableName.name);
        if (existingPromise !== undefined) {
            return await existingPromise;
        }

        const promise = this.backgroundLoadNativeTableSnapshot(appID, tableName);
        this.snapshotPromisesByAppID.get(appID).nativeTables.set(tableName.name, promise);

        return await promise;
    }

    public persistPublishedApp(appID: string, publishedApp: PublishedAppSnapshot): void {
        const key = publishedAppKeyForID(appID);
        // Ensure we don't get stuck on extremely old apps if anything goes wrong.
        // At the worst, we fail somewhere in the middle and have to revert to
        // slower loading.
        localStorageRemoveItem(key);
        localStorageSetItem(key, zipPublishedAppString(publishedApp));
    }

    public setInitialSchemaForAppID(appID: string, schema: TypeSchema): void {
        this.initialSchemasByAppID.set(appID, { ...schema });
    }

    public getInitialSchemaForAppID(appID: string): TypeSchema | undefined {
        return this.initialSchemasByAppID.get(appID);
    }

    public trackEvent<Name extends keyof AppAnalyticsEvents>(event: Name, options: AppAnalyticsEvents[Name]): void {
        trackEvent(event, options as AnalyticsEvents[Name]);
    }
}
